import { AfterViewInit, Component, computed, effect, inject, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { finalize } from 'rxjs';
import { LocationService } from '../../../../service/location.service';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import { TransactionsService } from '../../../../service/transactions.service';
import {
  AssignmentDetails,
  AssignmentTimelineEntry,
  FeatureFlag,
  IncidentFormContext,
  LiveCamera,
  LocationResponse,
  ReviewSubjectType,
  TimelineRow,
  VideoClip,
  VideoSnapshot,
} from '../../../../types';
import { ReviewFormService } from '../review-submit-form/review-form.service';
import { FormBuilder } from '@angular/forms';
import { NotifyService } from 'src/app/service/notify.service';
import { PosthogService } from 'src/app/service/posthog.service';
import { getLpReviewUrl } from '../review-utils';
import { AssignmentDetailsStore } from '../investigation-assignment/assignment-details.store';

@Component({
  selector: 'app-transaction-incident-form',
  templateUrl: './transaction-incident-form.component.html',
  styleUrl: './transaction-incident-form.component.scss',
  standalone: false,
})
export class TransactionIncidentFormComponent implements AfterViewInit {
  public reviewFormService = inject(ReviewFormService);
  private lpService = inject(LossPreventionService);
  private locationService = inject(LocationService);
  private transactionsService = inject(TransactionsService);
  private fb = inject(FormBuilder);
  private notifyService = inject(NotifyService);
  private assignmentDetailsStore = inject(AssignmentDetailsStore);
  private posthogService = inject(PosthogService);

  public incidentDateEditable = input<boolean>(false);
  public locationTimezone = input<string | undefined>();
  public isLoading = signal<boolean>(false);
  public oneNotUsefulSubmitted = signal<boolean>(false);
  public videoExpanded = signal<boolean>(false);

  public soldTransactions = signal<AssignmentTimelineEntry[]>([]);
  public cancellationUniqueId = signal<string | undefined>(undefined);
  public cancelledDate = signal<string>('');

  // View More state
  public hasMoreTransactions = signal<boolean>(true);
  public isLoadingMoreTransactions = signal<boolean>(false);
  public noMoreData = signal<boolean>(false);
  public lastAfterTransactionTime = signal<Date | undefined>(undefined);

  public recentWalkins = signal<VideoSnapshot[]>([]);

  // these are clips pre-downloaded by the backend
  public videoClips = signal<VideoClip[]>([]);
  // these are branch & location details in case video clips aren't available
  public location = signal<LocationResponse | undefined>(undefined);
  // these are external links to the market cameras in case video clips aren't available
  public liveCameras = signal<LiveCamera[] | undefined>(undefined);
  // (optional) instructions included by a loss-prevention team lead
  public instructions = signal<string | undefined>(undefined);
  public isExtraLinkRequired = signal<boolean>(false);
  // give parent control of where this form will be projected
  public formRef = viewChild<TemplateRef<unknown>>('formBody');
  public formRefReady = output<TemplateRef<unknown>>();
  // give parent info they might want from this incident form
  public contextReady = output<IncidentFormContext>();

  isAllClipsNotUseful() {
    const clips = this.videoClips();

    if (!clips || clips.length === 0) return true;
    return clips.every((clip) => clip.not_useful_submitted === true);
  }

  public filteredSoldTransactions = computed(() => {
    return this.soldTransactions();
  });

  public timelineRows = computed<TimelineRow[]>(() => {
    return this.filteredSoldTransactions().map((entry) => {
      const itemsStr = entry.items?.map((i) => `${i.description} ($${Number(i.total).toFixed(2)})`).join('\n') ?? '';
      return {
        when: entry.occurred,
        timestamp: entry.timestamp,
        items: itemsStr,
        highlight: entry.occurred === 'cancellation' || entry.occurred === 'underringing',
        transactionId: entry.platform_id,
      };
    });
  });

  public isCancellation = computed(() => this.assignmentDetailsStore.details()?.subject_type === ReviewSubjectType.Cancellation);

  public showLpCameras = computed(
    () => this.isCancellation() && this.posthogService.isFeatureEnabledByEnum(FeatureFlag.EnableLpCameraLinks),
  );

  public lpCameraLinks = computed(() => {
    const details = this.assignmentDetailsStore.details();
    if (!details?.subject_id) return [];
    const base = getLpReviewUrl(details.assignment_id);
    const transaction = encodeURIComponent(details.subject_id);
    return (this.liveCameras() ?? []).map((camera) => ({
      url: `${base}?transaction=${transaction}&camera=${encodeURIComponent(camera.provider_camera_id)}`,
    }));
  });

  constructor() {
    effect(() => {
      const details = this.assignmentDetailsStore.details();
      if (!details) return;
      this.loadAssignment(details);
    });
  }

  ngAfterViewInit(): void {
    this.formRefReady.emit(this.formRef());
  }

  submitNotUseful({ videoClipId, clipNotUsefulReason }) {
    this.lpService.reportVideoClip(this.assignmentDetailsStore.details()!.assignment_id, clipNotUsefulReason, videoClipId).subscribe(() => {
      this.notifyService.notify('Clip successfully reported');
      this.oneNotUsefulSubmitted.set(true);
      this.videoClips.update((clips) => clips.map((clip) => (clip.id === videoClipId ? { ...clip, not_useful_submitted: true } : clip)));
      if (this.isAllClipsNotUseful()) {
        this.isExtraLinkRequired.set(true);
      }
    });
  }

  loadMoreTransactions() {
    const uniqueId = this.cancellationUniqueId();
    const afterTime = this.lastAfterTransactionTime();

    if (!uniqueId || !afterTime) return;

    this.isLoadingMoreTransactions.set(true);
    this.transactionsService
      .getMoreTransactions(uniqueId, afterTime)
      .pipe(finalize(() => this.isLoadingMoreTransactions.set(false)))
      .subscribe((result) => {
        if (result.transactions.length > 0) {
          const newEntries: AssignmentTimelineEntry[] = result.transactions.map((txn) => ({
            timestamp: txn.time_sold,
            occurred: 'after',
            transaction_id: String(txn.transaction_id),
            platform_id: txn.platform_id ?? undefined,
            items: txn.items.map((i) => ({ description: i.description, total: i.total })),
          }));

          this.soldTransactions.update((current) => [...current, ...newEntries]);

          if (result.last_transaction_time) {
            this.lastAfterTransactionTime.set(new Date(result.last_transaction_time));
          }
        } else {
          const currentTime = this.lastAfterTransactionTime();
          if (currentTime) {
            this.lastAfterTransactionTime.set(new Date(currentTime.getTime() + 60 * 60 * 1000));
          }
        }

        this.hasMoreTransactions.set(result.has_more);
        this.noMoreData.set(!result.has_more);
      });
  }

  private loadAssignment(details: AssignmentDetails) {
    if (!details) return;

    const timeline = details.transaction_details?.timeline ?? [];
    const txnDate = details.transaction_details?.transaction?.date;

    // transactions table stuff
    this.soldTransactions.set(timeline);
    this.cancelledDate.set(txnDate ?? '');

    // Initialize the last after transaction time for "View More" functionality
    const afterTransactions = timeline.filter((t) => t.occurred === 'after');
    if (afterTransactions.length > 0) {
      const latestAfterTime = afterTransactions.map((t) => new Date(t.timestamp)).reduce((max, current) => (current > max ? current : max));
      this.lastAfterTransactionTime.set(latestAfterTime);
    } else if (txnDate) {
      // If no "after" transactions, start from the cancellation time
      this.lastAfterTransactionTime.set(new Date(txnDate));
    }

    // "recent walk-ins" - these are human activities, 1 hour before and after the transaction
    this.recentWalkins.set(details.video_snapshots ?? []);

    this.cancellationUniqueId.set(details.subject_id);

    this.videoClips.set(details.video_clips);

    // check is one not useful button submitted
    this.oneNotUsefulSubmitted.set(details.video_clips.some((clip) => clip.not_useful_submitted));

    // and also external links to the cameras
    this.liveCameras.set(details.live_streams);

    // miscellaneous
    this.instructions.set(details.instructions);

    const form = this.reviewFormService.form;
    // incident date shall be readonly, inferred from cancelled date
    if (txnDate) form.controls.instance_at.setValue(new Date(txnDate));
    this.reviewFormService.caseImages.set(details.case_images);
    // cancellation items as suspected items
    const txnItems = details.transaction_details?.transaction?.items ?? [];
    txnItems.forEach((item) => form.controls.suspectedItems.push(this.fb.control(item)));

    this.contextReady.emit({
      locationId: details.location_id,
      instanceAtDate: txnDate ? new Date(txnDate) : undefined,
      form: form,
    });

    this.isExtraLinkRequired.set(this.isAllClipsNotUseful());
    this.locationService.getLocation({ locationId: details.location_id }).subscribe((location) => this.location.set(location));
  }
}
